import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Typography,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Skeleton,
  CardContent,
  Tooltip,
  Chip,
  Autocomplete,
  Modal,
  Card,
  InputAdornment,
  Badge,
  Avatar,
  Fade,
  Zoom,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
// import EditIcon from '@mui/icons-material/Edit';
import { styled } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import FilterListIcon from "@mui/icons-material/FilterList";
import SellIcon from "@mui/icons-material/Sell";
import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import axios from "axios";
import AddDrugModal from "./components/AddDrugModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";


const baseurl = "https://api.medstown.com";
//
// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  borderRadius: 20,
  padding: theme.spacing(3),
  boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: "1px solid rgba(226, 232, 240, 0.8)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  borderRadius: 12,
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  background: active
    ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
    : "rgba(255, 255, 255, 0.8)",
  color: active ? "#ffffff" : "#64748b",
  border: `1px solid ${active ? "#3b82f6" : "rgba(226, 232, 240, 0.8)"}`,
  boxShadow: active ? "0 8px 25px rgba(59, 130, 246, 0.3)" : "none",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: active
      ? "0 12px 30px rgba(59, 130, 246, 0.4)"
      : "0 8px 20px rgba(0, 0, 0, 0.1)",
    background: active
      ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
      : "rgba(255, 255, 255, 1)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 16,
  overflow: "auto", // Enable horizontal scrolling
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.08)",
  border: "1px solid rgba(226, 232, 240, 0.5)",
  background: "#ffffff",
  // Custom scrollbar styling
  "&::-webkit-scrollbar": {
    height: 8,
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#c1c1c1",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#a8a8a8",
    },
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.875rem",
    color: "#374151",
    borderBottom: "2px solid #e5e7eb",
    padding: theme.spacing(2),
    whiteSpace: "nowrap", // Prevent header text wrapping
    minWidth: 120, // Minimum width for columns
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    transform: "translateX(4px)",
    boxShadow: "4px 0 12px rgba(59, 130, 246, 0.1)",
  },
  "& .MuiTableCell-root": {
    borderBottom: "1px solid #f1f5f9",
    padding: theme.spacing(1.5, 2),
    whiteSpace: "nowrap", // Prevent cell content wrapping
    minWidth: 120, // Minimum width for columns
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 1200, // Set minimum table width to enable horizontal scroll
  tableLayout: "auto", // Allow table to expand beyond container
}));

const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ModalContent = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  borderRadius: 24,
  padding: theme.spacing(4),
  minWidth: 500,
  maxWidth: "90vw",
  border: "1px solid rgba(226, 232, 240, 0.5)",
  boxShadow: "0 25px 80px rgba(0, 0, 0, 0.15)",
  outline: "none",
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontWeight: 600,
  padding: "8px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
  },
}));

// Debounce helper
function debounce(func, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
}

// Tag color mapping
const getTagColor = (tag) => {
  const tagColors = {
    PWC: { border: "#dc2626", text: "#dc2626", bg: "#fef2f2" },
    DRUGS: { border: "#2563eb", text: "#2563eb", bg: "#eff6ff" },
    AYURVEDA: { border: "#059669", text: "#059669", bg: "#ecfdf5" },
    HOMEO: { border: "#7c3aed", text: "#7c3aed", bg: "#f3e8ff" },
    OTC: { border: "#ea580c", text: "#ea580c", bg: "#fff7ed" },
    PRESCRIPTION: { border: "#0891b2", text: "#0891b2", bg: "#ecfeff" },
    TABLET: { border: "#be123c", text: "#be123c", bg: "#fff1f2" },
    SYRUP: { border: "#a855f7", text: "#a855f7", bg: "#faf5ff" },
    INJECTION: { border: "#dc2626", text: "#dc2626", bg: "#fef2f2" },
    CAPSULE: { border: "#0d9488", text: "#0d9488", bg: "#f0fdfa" },
  };

  const upperTag = tag.toUpperCase();
  return (
    tagColors[upperTag] || { border: "#6b7280", text: "#6b7280", bg: "#f9fafb" }
  );
};

export default function DrugsProduct({ type }) {
  const [data, setData] = useState([]);
  const [letterFilter, setLetterFilter] = useState("a");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const navigation = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [availableTags, setAvailableTags] = useState(
    ["drugs", "pwc", "ayurveda", "Homeo"].map((tag) => tag.toUpperCase())
  );
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [editPrice, setEditPrice] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [activeMedicine, setActiveMedicine] = useState(null);
  const [tagInput, setTagInput] = useState([]);

  const newTags = tagInput.filter((tag) => !availableTags.includes(tag));
  if (newTags.length) {
    setAvailableTags((prev) => [...new Set([...prev, ...newTags])]);
  }

  // Update debounced search term with 300ms delay
  useEffect(() => {
    const handler = debounce(() => {
      if (searchTerm.trim() === "") {
        setLetterFilter("a");
      }
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    handler();
  }, [searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = "";

      if (debouncedSearchTerm) {
        url = `${baseurl}/api/medshubs/drugs/search?page=${
          page + 1
        }&limit=${rowsPerPage}&search=${encodeURIComponent(
          debouncedSearchTerm
        )}`;
      } else {
        url = `${baseurl}/api/medshubs/alphabetical-drugs?page=${
          page + 1
        }&limit=${rowsPerPage}${letterFilter ? `&value=${letterFilter}` : ""}`;
      }

      const res = await axios.get(url);
      setData(res.data?.data?.list || []);
      setTotalCount(res.data?.data?.totalCount || 0);
    } catch (err) {
      console.error("Error fetching drugs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, letterFilter, debouncedSearchTerm]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const filteredData = data;
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Handle status toggle (Active/Inactive)
  const handleStatusToggle = async (item) => {
    try {
      const payload = {
        status: !item.status,
      };

      console.log(
        "Updating status for:",
        item.name,
        "New status:",
        !item.status
      );

      const response = await axios.patch(
        `${baseurl}/api/medshubs/drugs/update/${item._id}`,
        payload
      );

      if (response.status === 200) {
        toast.success(
          !item.status
            ? "Medicine activated successfully!"
            : "Medicine deactivated successfully!"
        );
        setData((prevData) =>
          prevData.map((d) =>
            d._id === item._id ? { ...d, status: !d.status } : d
          )
        );
        // fetchData(); // Refresh data
      } else {
        toast.error("Status update failed");
      }
    } catch (error) {
      console.error("Failed to update status:", error.response?.data || error);
      toast.error("Failed to update medicine status");
    }
  };

  // Handle out of stock toggle
  const handleOutOfStockToggle = async (item) => {
    try {
      const payload = {
        is_discontinued: !item.is_discontinued,
      };

      const response = await axios.patch(
        `${baseurl}/api/medshubs/drugs/update/${item._id}`,
        payload
      );

      if (response.status === 200) {
        toast.success(
          !item.is_discontinued
            ? "Medicine marked as out of stock!"
            : "Medicine marked as in stock!"
        );
        // fetchData();
        setData((prevData) =>
          prevData.map((d) =>
            d._id === item._id
              ? { ...d, is_discontinued: !d.is_discontinued }
              : d
          )
        );
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      console.error(
        "Failed to update stock status:",
        error.response?.data || error
      );
      toast.error("Failed to update stock status");
    }
  };

  return (
    <Box p={isMobile ? 2 : 1}>
      {/* Header Stats Card */}
      <StyledCard sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.2)",
                width: 56,
                height: 56,
              }}
            >
              <LocalPharmacyIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                  />
                ) : (
                  totalCount.toLocaleString()
                )}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Total Medicines Available
              </Typography>
            </Box>
          </Box>
          <AddDrugModal />
        </Box>
      </StyledCard>

      {/* Search & Filter Section */}
      <SearchContainer>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SearchIcon sx={{ color: "#64748b", fontSize: 24 }} />
          <Typography variant="h6" fontWeight={600} color="#374151">
            Search & Filter Medicines
          </Typography>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search medicines by name, composition, or SKU..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setLetterFilter("");
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
              "& fieldset": {
                borderColor: "rgba(226, 232, 240, 0.8)",
              },
              "&:hover fieldset": {
                borderColor: "#3b82f6",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3b82f6",
                borderWidth: "2px",
              },
            },
          }}
        />

        {/* Alphabet Filter */}
        <Box mt={3}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterListIcon sx={{ color: "#64748b" }} />
            <Typography variant="subtitle1" fontWeight={600} color="#374151">
              Filter by First Letter
            </Typography>
          </Box>
          <Box sx={{ overflowX: "auto", pb: 1 }}>
            <Box display="flex" gap={1} minWidth="max-content">
              {letters.map((char) => (
                <FilterButton
                  key={char}
                  size="small"
                  active={letterFilter === char.toLowerCase()}
                  onClick={() => {
                    setLetterFilter(char.toLowerCase());
                    setSearchTerm("");
                    setPage(0);
                  }}
                >
                  {char}
                </FilterButton>
              ))}
            </Box>
          </Box>
        </Box>
      </SearchContainer>

      {/* Horizontal Scroll Notice */}
      <Box
        sx={{
          mb: 2,
          p: 1,
          backgroundColor: "#f0f9ff",
          borderRadius: 2,
          border: "1px solid #0ea5e9",
          display: { xs: "block", lg: "none" },
        }}
      >
        <Typography variant="caption" color="#0369a1" fontWeight={600}>
          💡 Scroll horizontally to view all columns
        </Typography>
      </Box>

      {/* Data Table with Horizontal Scroll */}
      <StyledTableContainer component={Paper}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Medicine Name</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Pack Size</TableCell>
              <TableCell sx={{ minWidth: 100 }}>Price</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Composition</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Tags</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Stock</TableCell>
              <TableCell sx={{ minWidth: 140 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(rowsPerPage)).map((_, index) => (
                <StyledTableRow key={index}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Skeleton variant="text" width={180} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Skeleton variant="text" width={100} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }}>
                    <Skeleton variant="text" width={80} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Skeleton variant="text" width={150} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Skeleton variant="text" width={100} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Skeleton variant="text" width={100} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Skeleton variant="text" width={100} height={24} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Skeleton variant="rectangular" width={120} height={32} />
                  </TableCell>
                </StyledTableRow>
              ))
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <Fade in={true} timeout={300 + index * 50} key={item._id}>
                  <StyledTableRow>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{
                          "&:hover": {
                            color: "#3b82f6",
                            fontWeight: 600,
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Chip
                        label={item.pack_size_label || "NA"}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <Box sx={{ display: "flex" }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="#059669"
                        >
                          ₹{item.price}
                        </Typography>
                        <Button
                          onClick={() => {
                            setActiveMedicine(item);
                            setEditPrice(item.price || "");
                            setPriceModalOpen(true);
                          }}
                          sx={{ padding: 0, margin: 0 }}
                        >
                          <EditIcon size={18} />
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Tooltip title={item.short_composition || "NA"}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            color: "#64748b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.short_composition || "NA"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {item.tags?.length > 0 ? (
                          item.tags.slice(0, 2).map((tag, tagIndex) => {
                            const tagColor = getTagColor(tag);
                            return (
                              <Chip
                                key={tagIndex}
                                label={tag.toUpperCase()}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  fontSize: "0.7rem",
                                  borderColor: tagColor.border,
                                  color: tagColor.text,
                                  backgroundColor: tagColor.bg,
                                }}
                              />
                            );
                          })
                        ) : (
                          <Chip
                            label="No Tags"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              borderColor: "#e5e7eb",
                              color: "#6b7280",
                              backgroundColor: "#f9fafb",
                            }}
                          />
                        )}
                        {item.tags?.length > 2 && (
                          <Chip
                            label={`+${item.tags.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 2, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Status Column with Switch */}
                    <TableCell sx={{ minWidth: 150 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.status || false}
                            onChange={() => handleStatusToggle(item)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#059669",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#059669",
                                },
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {item.status ? (
                              <CheckCircleIcon
                                sx={{ fontSize: 14, color: "#059669" }}
                              />
                            ) : (
                              <BlockIcon
                                sx={{ fontSize: 14, color: "#dc2626" }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: item.status ? "#059669" : "#dc2626",
                                fontWeight: 600,
                              }}
                            >
                              {item.status ? "Active" : "Inactive"}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    </TableCell>

                    {/* Stock Column */}
                    <TableCell sx={{ minWidth: 150 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!item.is_discontinued} // Switch should be ON when IN STOCK (is_discontinued = false)
                            onChange={() => handleOutOfStockToggle(item)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#059669", // Green when IN STOCK
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#059669", // Green track when IN STOCK
                                },
                              "& .MuiSwitch-switchBase": {
                                color: "#dc2626", // Red when OUT OF STOCK
                              },
                              "& .MuiSwitch-track": {
                                backgroundColor: "#dc2626", // Red track when OUT OF STOCK
                              },
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {item.is_discontinued ? (
                              <BlockIcon
                                sx={{ fontSize: 14, color: "#dc2626" }}
                              />
                            ) : (
                              <CheckCircleIcon
                                sx={{ fontSize: 14, color: "#059669" }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: item.is_discontinued
                                  ? "#dc2626"
                                  : "#059669",
                                fontWeight: 600,
                              }}
                            >
                              {item.is_discontinued
                                ? "Out of Stock"
                                : "In Stock"}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 140 }} align="center">
                      <ActionButton
                        size="small"
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setActiveMedicine(item);
                          setTagInput(item.tags || []);
                          setTagModalOpen(true);
                        }}
                        sx={{
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                          borderRadius: 2,
                          fontSize: "0.75rem",
                          minWidth: 110,
                        }}
                      >
                        Edit Tags
                      </ActionButton>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          navigation(
                            `/admin/durgs-individual-page/${item?._id}`
                          )
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {/* delete */}
                      <IconButton
                        color="error"
                        onClick={async () => {
                          if (
                            window.confirm(
                              "Are you sure you want to delete this medicine?"
                            )
                          ) {
                            try {
                              const response = await axios.delete(
                                `${baseurl}/api/medshubs/drugs/${item._id}`
                              );
                              if (response.status === 200) {
                                toast.success("Medicine deleted successfully!");
                                fetchData(); // Refresh table
                              } else {
                                toast.error("Failed to delete medicine");
                              }
                            } catch (error) {
                              console.error(
                                "Failed to delete medicine:",
                                error
                              );
                              toast.error("Failed to delete medicine");
                            }
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                </Fade>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={4}>
                    <LocalPharmacyIcon
                      sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }}
                    />
                    <Typography variant="h6" color="#64748b">
                      No Medicines Found
                    </Typography>
                    <Typography variant="body2" color="#94a3b8">
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            borderTop: "1px solid #f1f5f9",
            "& .MuiTablePagination-toolbar": {
              backgroundColor: "#f8fafc",
            },
          }}
        />
      </StyledTableContainer>

      {/* Tag Edit Modal */}
      <StyledModal open={tagModalOpen} onClose={() => setTagModalOpen(false)}>
        <Zoom in={tagModalOpen}>
          <ModalContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: "#3b82f6" }}>
                <EditIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Edit Tags
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {activeMedicine?.name}
                </Typography>
              </Box>
            </Box>

            <Autocomplete
              multiple
              freeSolo
              options={availableTags}
              value={tagInput}
              onChange={(e, newValue) => setTagInput(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagColor = getTagColor(option);
                  const isNew = !availableTags.includes(option);

                  return (
                    <Chip
                      key={option}
                      label={option + (isNew ? " (new)" : "")}
                      variant="outlined"
                      {...getTagProps({ index })}
                      sx={{
                        borderRadius: 2,
                        m: 0.25,
                        borderColor: isNew ? "#059669" : tagColor.border,
                        color: isNew ? "#059669" : tagColor.text,
                        backgroundColor: isNew ? "#ecfdf5" : tagColor.bg,
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Medicine Tags"
                  placeholder="Add tags..."
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                    },
                  }}
                />
              )}
            />

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ActionButton
                variant="outlined"
                onClick={() => setTagModalOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={async () => {
                  const payload = {
                    type: "prescriptions",
                    sku_id: activeMedicine?.sku_id,
                    tags: tagInput,
                  };

                  try {
                    const response = await axios.post(
                      "https://api.medstown.com/customer/tags/medshub/update",
                      payload
                    );

                    toast.success("Tags updated successfully!");
                    fetchData();
                    setTagModalOpen(false);
                  } catch (error) {
                    console.error("Failed to update tags:", error);
                    toast.error("Failed to update tags");
                  }
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  borderRadius: 2,
                }}
              >
                Save Changes
              </ActionButton>
            </Box>
          </ModalContent>
        </Zoom>
      </StyledModal>

      {/* Price Edit Model */}
      <StyledModal
        open={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
      >
        <Zoom in={priceModalOpen}>
          <ModalContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: "#f59e0b" }}>
                <SellIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Edit Price
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {activeMedicine?.name}
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Price (₹)"
              type="number"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ActionButton
                variant="outlined"
                onClick={() => setPriceModalOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={async () => {
                  try {
                    const type = "drugs"; // default drugs
                    const payload = {
                      price: editPrice,
                    };

                    const response = await axios.put(
                      `${baseurl}/api/medshubs/update-price/${type}/${activeMedicine?.sku_id}`,
                      payload
                    );

                    if (response.status === 200) {
                      toast.success("Price updated successfully!");
                      fetchData(); // Refresh table
                      setPriceModalOpen(false);
                    } else {
                      toast.error("Failed to update price");
                    }
                  } catch (error) {
                    console.error("Failed to update price:", error);
                    toast.error("Failed to update price");
                  }
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: 2,
                }}
              >
                Save Changes
              </ActionButton>
            </Box>
          </ModalContent>
        </Zoom>
      </StyledModal>
    </Box>
  );
}
